import { useMemo } from "react";
import { useNotifications, useUnreadNotificationsCount } from "../api/useNotifications";
import { useMarkAsRead } from "../api/useMarkAsRead";
import { useDismissNotification } from "../api/useDismissNotification";
import { NotificationService } from "../services/notificationService";
export function useNotificationCenter(workspaceId, filters) {
    const { data: notifications, error, isLoading, refetch } = useNotifications(workspaceId);
    const { data: unreadData, refetch: refetchUnread } = useUnreadNotificationsCount(workspaceId);
    const { markAsRead, markAllAsRead, isLoading: isMarkingRead } = useMarkAsRead(workspaceId);
    const { dismissNotification, isLoading: isDismissing } = useDismissNotification(workspaceId);
    const notificationList = notifications || [];
    const filteredNotifications = useMemo(() => {
        if (!filters)
            return notificationList;
        return NotificationService.filterNotifications(notificationList, filters);
    }, [notificationList, filters]);
    const summary = useMemo(() => {
        return NotificationService.getNotificationSummary(filteredNotifications);
    }, [filteredNotifications]);
    const handleMarkAsRead = async (notificationId) => {
        try {
            await markAsRead(notificationId);
            // Refresh data after successful operation
            await Promise.all([refetch(), refetchUnread()]);
        }
        catch (error) {
            throw error;
        }
    };
    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead();
            // Refresh data after successful operation
            await Promise.all([refetch(), refetchUnread()]);
        }
        catch (error) {
            throw error;
        }
    };
    const handleDismiss = async (notificationId) => {
        try {
            await dismissNotification(notificationId);
            // Refresh data after successful operation
            await Promise.all([refetch(), refetchUnread()]);
        }
        catch (error) {
            throw error;
        }
    };
    const handleRefresh = async () => {
        await Promise.all([refetch(), refetchUnread()]);
    };
    return {
        notifications: filteredNotifications,
        summary,
        unreadCount: unreadData?.count ?? 0,
        isLoading,
        error,
        actions: {
            markAsRead: handleMarkAsRead,
            markAllAsRead: handleMarkAllAsRead,
            dismiss: handleDismiss,
            refresh: handleRefresh,
        },
        isProcessing: isMarkingRead || isDismissing,
    };
}
